import { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getVideoCalls, isLoadingCalls, getNextToken } from 'selectors/ecsSelectors';
import { PageHeader } from 'ui/shared/pageHeader';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from 'hooks/useWebSocket/useWebSocket';
import { ecsListLoading, receiveEcsListMessage } from 'actions/ecsActions';
import { CallId, VideoCall, VideoCallMap } from 'shared/models';
import { VideoCallRecordSet } from '../videoCallRecordSet/VideoCallRecordSet';
import { callApi } from 'apiClient';
import { LoadingSpinner } from 'ui/shared/LoadingSpinner';
import { Loading } from 'ui/shared/loading';

require('./VideoListPage.css');


export const VideoListPage = () => {
    const { t } = useTranslation();
    const websocketClient = useWebSocket();
    const refreshIntervalRef = useRef<NodeJS.Timeout>();

    const videoCalls = useSelector(getVideoCalls);
    const isLoading = useSelector(isLoadingCalls);
    const nextToken = useSelector(getNextToken);

    const [ pendingOrder, setPendingOrder ] = useState<CallId[]>([]);
    const [ activeOrder, setActiveOrder ] = useState<CallId[]>([]);
    const [ finishedOrder, setFinishedOrder ] = useState<CallId[]>([]);

    const dispatch = useDispatch();

    const loadVideoCalls = useCallback((nextToken: string | null) => {
        dispatch(ecsListLoading());
        void (async () => {
            try {
                const cacheBust = Math.random().toString(28).substr(2);
                const queryString = `?${nextToken ? `next=${nextToken}&` : ''}${cacheBust}`;
                const result: { calls: VideoCall[]; nextToken: string | null } = await callApi('GET', `/api/video/recent${queryString}`);
                const callMap = result.calls.reduce((obj, call) => ({ ...obj, [`${call.callId}`]: call }), {} as Record<CallId, VideoCall>);
                dispatch(receiveEcsListMessage(callMap, { nextToken: result.nextToken }));
            }
            catch (err) {
                console.error('Failed to get recent video calls', err);
            }
        })();
    }, [ dispatch ]);

    useEffect(() => {
        // Register for notifications of new and updated calls
        websocketClient.ecsRegister();

        // Load the initial list of calls
        loadVideoCalls(null);
        
        // Unregister from call notifications when the page exits
        return () => websocketClient.ecsUnregister();
    }, [ websocketClient, loadVideoCalls, dispatch ]);

    const handleVisibilityChange = useCallback(() => {
        console.log(`Page is now ${document.hidden ? 'hidden' : 'visible'}`);
        if (!document.hidden) {
            loadVideoCalls(null);
        }
    }, [ loadVideoCalls ]);

    useEffect(() => {
        document.addEventListener('visibilitychange', handleVisibilityChange, false);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange, false);
        };
    }, [ handleVisibilityChange ]);

    useEffect(() => {
        // Load more rows until we have 30
        if (nextToken && !isLoading && Object.keys(videoCalls).length < 30) {
            loadVideoCalls(nextToken);
        }
    }, [ videoCalls, nextToken, isLoading, loadVideoCalls ]);

    const handleClickShowMore = useCallback(() => {
        loadVideoCalls(nextToken);
    }, [ nextToken, loadVideoCalls ]);

    const UNANSWERED_CALL_EXPIRY = 15 * 60 * 1000; // 15 minutes
    const MAX_NO_UPDATES_AGE = 60 * 60 * 1000; // 60 minutes

    const handleTick = useCallback((videoCalls: VideoCallMap) => {
        const now = Date.now();

        setPendingOrder(
            Object.keys(videoCalls)
                .filter(id =>
                    !videoCalls[id].joined &&
                    !videoCalls[id].ended &&
                    videoCalls[id].source === 'EL' &&
                    (now - videoCalls[id].created < UNANSWERED_CALL_EXPIRY) // hasn't expired
                )
                .sort((a, b) => videoCalls[a].created - videoCalls[b].created) // First-created first
        );
    
        setActiveOrder(
            Object.keys(videoCalls)
                .filter(id =>
                    !!videoCalls[id].joined &&
                    !videoCalls[id].ended &&
                    (now - videoCalls[id].updated < MAX_NO_UPDATES_AGE)
                )
                .sort((a, b) => videoCalls[b].joined! - videoCalls[a].joined!) // Most-recently-joined first
        );
    
        setFinishedOrder(
            Object.keys(videoCalls)
                .filter(id =>
                    (now - videoCalls[id].updated < MAX_NO_UPDATES_AGE) &&
                    (
                        !!videoCalls[id].ended ||
                        (
                            !videoCalls[id].joined &&
                            !videoCalls[id].ended &&
                            (now - videoCalls[id].created >= UNANSWERED_CALL_EXPIRY) // has expired
                        )
                    )
                )
                .sort((a, b) =>
                    // Most-recently-ended first (or if unanswered, when the call was created)
                    (videoCalls[b].ended || videoCalls[b].created) - (videoCalls[a].ended || videoCalls[a].created)
                )
        );
    }, [ setPendingOrder, setActiveOrder, setFinishedOrder, UNANSWERED_CALL_EXPIRY, MAX_NO_UPDATES_AGE ]);

    useEffect(() => {
        // Do one page refresh now since the video calls list is new
        handleTick(videoCalls);

        // Refresh the page every second when there are calls on the page.
        // (i.e. no need to refresh if the page is empty)
        if (Object.keys(videoCalls).length > 0) {
            refreshIntervalRef.current = setInterval(() => handleTick(videoCalls), 1000);
        }
        return () => clearInterval(refreshIntervalRef.current!);
    }, [ videoCalls, refreshIntervalRef, handleTick ]);

    return (
        <div id="video-list-page">
            <PageHeader title={ t('VIDEO.TITLE') }>
                { isLoading &&
                    <div className="ecs-loading">
                        <LoadingSpinner size="tiny"/>
                        <Loading/>
                    </div>
                }
            </PageHeader>
            <div id="page-content">
                <div className="grid">
                    <header className="list-name">
                        <h2>
                            { t('VIDEO.LIST.INCOMING') }
                        </h2>
                    </header>
                    <VideoCallRecordSet
                        type="pending"
                        callIds={ pendingOrder }
                    />
                    
                    <header className="list-name">
                        <h2>
                            { t('VIDEO.LIST.ACTIVE') }
                        </h2>
                    </header>
                    <VideoCallRecordSet
                        type="active"
                        callIds={ activeOrder }
                    />

                    <header className="list-name">
                        <h2>
                            { t('VIDEO.LIST.RECENT') }
                        </h2>
                    </header>
                    <VideoCallRecordSet
                        type="recent"
                        callIds={ finishedOrder }
                    />
                
                    { nextToken &&
                        <button
                            className="ui primary button"
                            disabled={ isLoading }
                            onClick={ handleClickShowMore }
                        >
                            { t('COMMAND.SHOW_MORE') }
                        </button>
                    }
                </div>
            </div>
        </div>
    );
};
